import { memo, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Radio } from "lucide-react";
import { coverageFromProbe } from "@/map/utils/video-probe-coverage";
import { cn } from "@/ui/lib/utils";
import type { MachineEvent } from "@/machines/types/event";
import MainPreview, {
  type AvailableTrack,
} from "@/machines/components/common/timeline-editor/main-preview/main-preview";
import { useGetVideoAround } from "@/machines/api/queries/video.queries.ts";
import { ROLLING_WINDOW_OPTIONS } from "@/machines/api/queries/events.queries.ts";
import { useTimelineEvents } from "@/machines/api/queries/use-timeline-events";
import { useVideoLibraryStore } from "@/machines/stores/useVideoLibraryStore.ts";
import { useTimelineVideoParams } from "@/machines/components/hooks/use-timeline-video-params";
import { useTimelineVideoAvailability } from "@/machines/components/hooks/use-timeline-video-availability";
import { useVideoCoverageStore } from "@/shared/stores/useVideoCoverageStore";
import { useDateTimeRangeStore, type DateTimeRangeValue } from "@/shared/stores/useDateTimeRangeStore.ts";
import type { MapEventRow } from "@/map/hooks/useLocationEvents";
import { LIVE_CURSOR_ANCHOR } from "@/map/hooks/useRealtimeWindow";
import { useLiveTimelineDomainContext } from "./live-timeline-domain-context";
import { TIMELINE_POLL_MS } from "./MapTimelineStack";

const VIDEO_WINDOW_CAP_S = 12 * 60 * 60;

/**
 * Cadence for rows the operator is not driving. Slower than the active row's 5s:
 * a background row needs to stay honest, not instant, and the cost multiplies by
 * every visible machine.
 */
const INACTIVE_ROW_POLL_MS = 25_000;

/** Compact timeline-body height — just the tracks, no chrome (see STACK_MAX_H). */
const ROW_BODY_H = 92;

interface MapTimelineRowProps {
  deviceMxid: string;
  machineName: string;
  /**
   * The compact (V2) stack already names the machine down its left edge, so the
   * row repeating it is wasted height. Only ever passed when ONE row is shown —
   * with several, each row's own name is the only thing telling them apart.
   */
  hideLabel?: boolean;
  machineColor?: string;
  /** The active row drives the video, cards, and live-follow; inactive rows are click-to-activate. */
  active: boolean;
  /** Activate this machine (row click). Omitted on the active row. */
  onActivate?: () => void;
  /** "realtime" fetches a rolling live window; otherwise the global date range. */
  isRealtime: boolean;
  /** Rolling window (realtime mode). */
  realtimeWindow: DateTimeRangeValue;
  /** Visible span of the realtime view. */
  liveSpanMs: number;
  /**
   * Alert-review override (ms): when set, the timeline freezes to a fixed window
   * centered on this moment (the selected alert/event) instead of the rolling
   * live window, so it's visible even when older than the live span. Null →
   * rolling live. Passed to every row so the whole stack recenters together.
   */
  reviewTime?: number | null;
  /** Active row only: non-edit segment click opens the on-map event card. */
  onEventOpen?: (event: MachineEvent, isTruckCycle: boolean) => void;
  /** Active row only: reports the track list for the shell's settings modal. */
  onAvailableTracks?: (tracks: AvailableTrack[]) => void;
  /** Active row only: reports the newest live event so the page can follow it. */
  onLiveLatestEvent?: (event: MapEventRow | null) => void;
  /**
   * Render this row's time axis. All rows share the same time window, so the
   * stack shows the axis only on the last (bottom) row — a single axis lines up
   * under every row's segments. Defaults to `true`.
   */
  showTimeAxis?: boolean;
}

/**
 * One machine's timeline inside the `/map` live-preview stack. Must render
 * inside a `MachineTimelineProvider` for this machine (the active row shares
 * the active provider that also hosts the video, so scrubbing stays synced;
 * inactive rows sit in their own provider).
 *
 * Every row renders its own video-availability overlay. Only the active row
 * polls at full rate, opens cards, and reports the live-latest event — inactive
 * rows fetch once and refresh on window-focus / the 5-minute realtime bucket flip.
 */
const MapTimelineRow = ({
  deviceMxid,
  machineName,
  hideLabel = false,
  machineColor,
  active,
  onActivate,
  isRealtime,
  realtimeWindow,
  liveSpanMs,
  reviewTime,
  onEventOpen,
  onAvailableTracks,
  onLiveLatestEvent,
  showTimeAxis = true,
}: MapTimelineRowProps) => {
  const { t } = useTranslation("map");
  const { value } = useDateTimeRangeStore();
  const fromDate = isRealtime ? realtimeWindow.fromDate : value.fromDate;
  const toDate = isRealtime ? realtimeWindow.toDate : value.toDate;

  // The stack's single domain + playhead. Null outside live mode, and null if a
  // row is somehow rendered outside the provider — in which case the row falls
  // back to computing its own, exactly as before.
  const { domain: sharedDomain, cursorMs: sharedCursorMs } = useLiveTimelineDomainContext();

  // Every row shows its own video-availability overlay — a row without it reads
  // as "no footage" rather than "not selected".
  //
  // Inactive rows used to fetch ONCE and never again, which left their strip
  // frozen at mount: clips are 30s, so a row you were not looking at fell a clip
  // behind every half minute and read as "this machine stopped recording" while
  // the active row showed fresh footage. They now poll on the same slow cadence
  // their events already use — just inside the clip interval, so a row is at
  // most about one clip behind rather than unboundedly stale. Hiding a row still
  // unmounts it and stops the asking entirely, which is what keeps this cheap on
  // a site with many machines.
  const rawVideoParams = useTimelineVideoParams(fromDate, toDate);
  const videoBeforeS = Math.min(rawVideoParams.timestamp_before, VIDEO_WINDOW_CAP_S);
  const videoAfterS = Math.min(rawVideoParams.timestamp_after, VIDEO_WINDOW_CAP_S);
  const { data: videos, status: videoStatus } = useGetVideoAround(
    {
      device_mxid: deviceMxid,
      timestamp: rawVideoParams.timestamp,
      timestamp_before: videoBeforeS,
      timestamp_after: videoAfterS,
      page: 1,
      itemsPerPage: 1000,
    },
    isRealtime
      ? {
          refetchInterval: active ? TIMELINE_POLL_MS : INACTIVE_ROW_POLL_MS,
          ...ROLLING_WINDOW_OPTIONS,
        }
      : undefined,
  );
  const videoSegments = useTimelineVideoAvailability(videos?.data, fromDate, toDate);

  // Record what THIS row's probe learned.
  //
  // The coverage store is what every row's "not asked yet" band is derived from,
  // but its only writers were in the video tile — which runs for the ACTIVE
  // machine alone. So an inactive row probed a window, got an answer, and told
  // nobody: `missingRanges` returned [] for a device with no record at all, and
  // the strip drew a plain gap, i.e. "no footage", where the honest answer was
  // "nobody asked". Inactive rows sat in that state far more often than the
  // active one — an asymmetry between rows showing the same window.
  //
  // Same shape as the tile's own recording (`useVideoData`): the returned rows'
  // extent when there is footage, the probed window when there is none.
  // `recordEmpty` itself ignores ranges too close to now, where "empty" only
  // means "not uploaded yet".
  const probedFromMs = rawVideoParams.timestamp.getTime() - videoBeforeS * 1000;
  const probedToMs = rawVideoParams.timestamp.getTime() + videoAfterS * 1000;
  useEffect(() => {
    if (videoStatus !== "success") return;
    const learned = coverageFromProbe(videos?.data, probedFromMs, probedToMs);
    const store = useVideoCoverageStore.getState();
    if (learned.kind === "covered") store.recordCovered(deviceMxid, learned.from, learned.to);
    else store.recordEmpty(deviceMxid, learned.from, learned.to);
  }, [videoStatus, videos, deviceMxid, probedFromMs, probedToMs]);

  // Stretches of this window nobody has probed for footage yet, so the strip can
  // say "not asked" instead of looking like "no footage". Recomputed from the
  // coverage the store actually holds — subscribing to the device's entry is what
  // makes it update when a fetch lands, and deriving it here means it cannot
  // drift from that record.
  const coverage = useVideoCoverageStore((s) => s.devices[deviceMxid]);
  const fromMs = fromDate.getTime();
  const toMs = toDate.getTime();
  const videoPendingRanges = useMemo(
    () => useVideoCoverageStore.getState().missingRanges(deviceMxid, fromMs, toMs),
    // `coverage` is a TRIGGER, not an input: it is the thing that changes when a
    // fetch lands, and `missingRanges` derives from the same record. Dropping it
    // would freeze the band at whatever the first render computed. Same shape as
    // `pendingRanges` in `use-timeline-events`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coverage, deviceMxid, fromMs, toMs],
  );

  // Share the fetched rows with the video tile. This strip's window is a
  // superset of the tile's (±1050s vs ±300s, same endpoint, same row type), so
  // handing them over lets a tile mounting alongside this row start with footage
  // instead of issuing its own request for a window we already have.
  useEffect(() => {
    const rows = videos?.data;
    if (rows?.length) useVideoLibraryStore.getState().addVideos(deviceMxid, rows);
  }, [videos?.data, deviceMxid]);

  // Active row only: observe events to report the newest live event up to the
  // page (drives the on-map detail card's live-follow). Same query key as the
  // row's MainPreview, so this adds no extra request.
  const { data: eventsData } = useTimelineEvents(
    {
      device_mxid: active && isRealtime ? deviceMxid : "",
      start_time: fromDate,
      end_time: toDate,
      page: 1,
      itemsPerPage: 20000,
    },
    { ...(isRealtime ? ROLLING_WINDOW_OPTIONS : {}), live: isRealtime },
  );

  const latestEventRow = useMemo((): MapEventRow | null => {
    if (!active || !isRealtime) return null;
    const list = eventsData?.data ?? [];
    if (list.length === 0) return null;
    let latest = list[0];
    for (const e of list) {
      if (new Date(e.startTime).getTime() > new Date(latest.startTime).getTime()) latest = e;
    }
    return {
      id: String(latest.id ?? latest.uuid),
      uuid: latest.uuid,
      deviceMxid,
      machineName,
      state: String(latest.state ?? "—"),
      startTime: new Date(latest.startTime),
      durationSeconds: latest.durationSeconds ?? 0,
      productive: !!latest.productive,
      color: latest.color || "#3b82f6",
    };
  }, [active, isRealtime, eventsData, deviceMxid, machineName]);

  // `latestEventRow` is a fresh object on every 5s poll (rebuilt with `new
  // Date(...)`), but the page only cares about its derived identity — id +
  // start + duration (which feed the live edge). Report up ONLY when that
  // identity actually changes, so an unchanged newest-event doesn't churn the
  // page's `setLiveLatestEvent` (→ full re-render + `liveEdge` recompute) every
  // poll. A genuine new event, a shifted start, or a growing duration all change
  // the signature and still propagate.
  const lastReportedSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active) {
      // Reactivating this row later must re-report even if its newest event is
      // unchanged (the page state was cleared/overwritten meanwhile).
      lastReportedSigRef.current = null;
      return;
    }
    const sig = latestEventRow
      ? `${latestEventRow.id}|${latestEventRow.startTime.getTime()}|${latestEventRow.durationSeconds}`
      : "none";
    if (sig === lastReportedSigRef.current) return;
    lastReportedSigRef.current = sig;
    onLiveLatestEvent?.(latestEventRow);
  }, [active, latestEventRow, onLiveLatestEvent]);
  // Clear the page's live-follow state when the active row unmounts.
  useEffect(() => {
    if (!active) return;
    return () => onLiveLatestEvent?.(null);
  }, [active, onLiveLatestEvent]);

  return (
    <div
      className={cn(
        "rounded-md transition-colors",
        active
          ? "bg-primary/[0.04]"
          : "cursor-pointer hover:bg-muted/30",
      )}
      // Any interaction with an inactive row activates it first (capture phase,
      // so it fires even if the timeline stops the bubbling click). The active
      // row keeps its normal timeline interactions.
      onPointerDownCapture={active ? undefined : onActivate}
    >
      {!hideLabel && (
      <div className="flex items-center gap-2 px-1.5 py-0.5">
        <span
          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: machineColor || "#EF4444" }}
        />
        <span className="truncate text-xs font-medium">{machineName}</span>
        {active ? (
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
            <Radio className="h-2.5 w-2.5" />
            {t("timelineRow.active")}
          </span>
        ) : (
          <span className="shrink-0 text-[10px] text-muted-foreground">{t("timelineRow.clickToActivate")}</span>
        )}
      </div>
      )}

      {/* `minHeight`, NOT `height`: the body's real content is the tracks SVG
          (sized from the machine's track count) plus the 30px time axis on the
          bottom row — measured at ~177px against the 92px this used to pin it
          to. `overflow` is visible, so nothing clipped: the row simply reported
          92px to the stack, the stack sized itself too small, and the axis spilled
          past the panel and off the bottom of the screen. A minimum keeps the
          compact baseline for short rows while letting a full one occupy the
          height it actually needs; STACK_MAX_H still caps the panel and scrolls. */}
      <div className="px-1 pb-1" style={{ minHeight: ROW_BODY_H }}>
        <MainPreview
          device_mxid={deviceMxid}
          videoSegments={videoSegments}
          videoPendingRanges={videoPendingRanges}
          compactToolbar
          onAvailableTracks={active ? onAvailableTracks : undefined}
          onEventOpen={active ? onEventOpen : undefined}
          // Live only. Range mode is historical review, and its window is the
          // whole global date range — polling that every 5s re-fetches a
          // multi-hour events+alerts payload for no benefit.
          //
          // Inactive rows poll TOO, just slower. They used to poll not at all
          // (each machine has its own query key, so no observer carried an
          // interval), which is why a row you weren't looking at showed whatever
          // it fetched on mount and never moved. Hiding a row is what makes this
          // cheap — a hidden row unmounts and stops asking entirely.
          refetchIntervalMs={
            isRealtime ? (active ? TIMELINE_POLL_MS : INACTIVE_ROW_POLL_MS) : undefined
          }
          timeWindow={isRealtime ? { fromDate, toDate, rolling: true } : undefined}
          liveViewSpanMs={isRealtime ? liveSpanMs : undefined}
          liveCursorAnchor={isRealtime ? LIVE_CURSOR_ANCHOR : undefined}
          // One domain and one playhead for the whole stack — see
          // LiveTimelineDomainProvider. Without these each row anchored on its
          // own cursor and nothing lined up under the shared axis.
          liveDomainOverride={isRealtime ? sharedDomain : null}
          sharedPlaybackMs={isRealtime ? sharedCursorMs : null}
          reviewTime={isRealtime ? reviewTime : undefined}
          suppressPendingEdits={!active}
          showTimeAxis={showTimeAxis}
        />
      </div>
    </div>
  );
};

// Memoized: one row per machine, all re-rendered on the parent's 5s poll / cursor
// ticks. Every prop is a primitive or a stable reference when unchanged (the
// page hands a stable per-machine `onActivate` and the rolling `realtimeWindow`
// only flips every 5 min), so the default shallow compare skips inactive rows
// whose inputs didn't change.
export default memo(MapTimelineRow);
